import { Alert, Box, Button, Card, CardContent, CardMedia, Chip, Container, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import RedeemIcon from '@mui/icons-material/Redeem'; // 선물상자 아이콘 import
import { Helmet } from 'react-helmet';
import { useSevenKnightsCouponPage } from './SevenKnightsCouponPage.hooks';
import Adsense from '../../Ads/Adsense';
import styles from './SevenKnightsCouponPage.module.css';

const SevenKnightsCouponPage = () => {
  const {
    memberCode,
    setMemberCode,
    couponCodes,
    setCouponCodes,
    history,
    handleSubmit,
    error,
    successMessage,
    isPending,
    isFormInvalid,
    claimedRewards,
    memberCodeHistory,
    removeMemberCodeFromHistory,
    KNOWN_COUPON_CODES_1,
    KNOWN_COUPON_CODES_2,
    loadKnownCoupons,
  } = useSevenKnightsCouponPage();

  return (
    <Container maxWidth="md">
      <Helmet>
        <title>세븐나이츠 쿠폰 등록기 - 한 번에 여러개 등록</title>
        <meta name="description" content="세븐나이츠 게임의 여러 쿠폰을 한 번에 쉽고 빠르게 등록하여 시간을 절약하세요. 알려진 쿠폰 목록을 불러올 수도 있습니다." />
      </Helmet>
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          세븐나이츠 쿠폰 등록
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mt: 2 }}>{successMessage}</Alert>}

        {claimedRewards.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              획득한 아이템
            </Typography>
            <Grid container spacing={1}>
              {claimedRewards.map((reward, index) => (
                <Grid item xs={3} sm={2} md={1.5} key={index}>
                  <Card className={styles.rewardCard}>
                    {reward.productImageUrl ? (
                      <CardMedia
                        component="img"
                        className={styles.rewardImage}
                        image={reward.productImageUrl}
                        alt={reward.productName || '보상 아이템'}
                      />
                    ) : (
                      <Box className={styles.rewardIconWrapper}>
                        <RedeemIcon className={styles.rewardIcon} />
                      </Box>
                    )}
                    <CardContent className={styles.rewardContent}>
                      <Typography variant="caption" component="div" className={styles.rewardName}>
                        {reward.productName || '이름 없는 아이템'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="memberCode"
            label="회원번호"
            name="memberCode"
            autoFocus
            value={memberCode}
            onChange={(e) => setMemberCode(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="couponCodes"
            label="쿠폰번호 (한 줄에 하나씩 입력)"
            id="couponCodes"
            multiline
            rows={4}
            value={couponCodes}
            onChange={(e) => setCouponCodes(e.target.value)}
          />
          <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
            <Grid item xs={6}>
              <Tooltip title="이 목록은 현재 사용 가능한 모든 쿠폰을 포함하고 있지 않으며, 일부는 만료되었을 수 있습니다." arrow>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => loadKnownCoupons(KNOWN_COUPON_CODES_1)}
                >
                  알려진 쿠폰 #1
                </Button>
              </Tooltip>
            </Grid>
            <Grid item xs={6}>
              <Tooltip title="이 목록은 현재 사용 가능한 모든 쿠폰을 포함하고 있지 않으며, 일부는 만료되었을 수 있습니다." arrow>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => loadKnownCoupons(KNOWN_COUPON_CODES_2)}
                >
                  알려진 쿠폰 #2
                </Button>
              </Tooltip>
            </Grid>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 1, mb: 2 }}
            disabled={isPending || isFormInvalid}
          >
            {isPending ? '등록 중...' : '등록'}
          </Button>
        </Box>

        {memberCodeHistory.length > 0 && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              최근 사용한 회원번호
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {memberCodeHistory.map((code) => (
                <Chip
                  key={code}
                  label={code}
                  onClick={() => setMemberCode(code)}
                  onDelete={() => removeMemberCodeFromHistory(code)}
                  clickable
                />
              ))}
            </Box>
          </Box>
        )}

        <Adsense adClient="ca-pub-9359050149154028" adSlot="4532367213" />
        <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          {memberCode ? (
            <>
              <Box component="span" sx={{ fontSize: '0.9rem', fontWeight: 'bold', color: 'primary.main', mr: 1, wordBreak: 'break-all' }}>
                {memberCode}
              </Box>
              <span>의 등록 내역</span>
            </>
          ) : (
            '등록 내역'
          )}
        </Typography>
        <TableContainer component={Paper}>
          <Table aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '70%' }}>쿠폰번호</TableCell>
                <TableCell align="right" sx={{ width: '30%' }}>등록일</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((row) => (
                <TableRow
                  key={row.coupon}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row" sx={{ wordBreak: 'break-all' }}>
                    {row.coupon}
                  </TableCell>
                  <TableCell align="right">{row.date}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
};

export default SevenKnightsCouponPage;
